import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Search, Upload, Eye, Trash2, X, ImageOff } from 'lucide-react';
import { Overlay } from '../../types';
import { overlaysApi } from '../../api/overlays';
import { Button } from '../UI/Button';
import { MachhaCharacter } from '../../lib/machhaAsset';
import { Modal } from '../UI/Modal';
import { Input } from '../UI/Input';
import { OverlayCardSkeleton } from '../UI/Skeleton';
import { track, EVENTS } from '../../lib/mixpanel';
import styles from './OverlayManager.module.css';

interface OverlayManagerProps {
  eventId: string;
  eventStatus: 'active' | 'ended';
  canManage?: boolean;
}

// The default Pixel Machha branding watermark is auto-applied to every photo.
// It must never surface in the overlays tab — so it can never be deleted —
// for any role (creator, admin, owner). Identified by its fixed storage path.
const DEFAULT_OVERLAY_S3_PATH = 'assets/overlays/whitefont.png';

// Dark-themed checkerboard that signals transparency
const CHECKERBOARD =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16'%3E" +
  "%3Crect width='8' height='8' fill='%232a2a2e'/%3E" +
  "%3Crect x='8' width='8' height='8' fill='%23222226'/%3E" +
  "%3Crect y='8' width='8' height='8' fill='%23222226'/%3E" +
  "%3Crect x='8' y='8' width='8' height='8' fill='%232a2a2e'/%3E" +
  "%3C/svg%3E\")";

// ── Overlay card ──────────────────────────────────────────────────────────────

interface OverlayCardProps {
  overlay: Overlay;
  eventStatus: 'active' | 'ended';
  canManage: boolean;
  onPreview: () => void;
  onDelete: () => void;
}

function OverlayCard({ overlay, eventStatus, canManage, onPreview, onDelete }: OverlayCardProps) {
  const [imgError, setImgError] = useState(false);

  return (
    <motion.div
      exit={{ opacity: 0, scale: 0.9 }}
      whileTap={{ scale: 0.96 }}
      transition={{ duration: 0.15 }}
      className={styles['overlay-card']}
    >
      {/* Thumbnail */}
      <div className={`${styles['card-thumbnail']} ${styles['checkerboard-bg']}`} style={{ '--checkerboard': CHECKERBOARD } as React.CSSProperties}>
        {imgError ? (
          <div className={styles['card-img-error']}>
            <ImageOff className={styles['card-img-error-icon']} />
            <span className={styles['card-img-error-text']}>Failed to load</span>
          </div>
        ) : (
          <img
            src={overlaysApi.fileUrl(overlay._id)}
            alt={overlay.name}
            className={styles['card-img']}
            onError={() => setImgError(true)}
          />
        )}

        {/* Desktop hover overlay */}
        <div className={styles['card-hover-bg']} />
        <div className={styles['card-hover-actions']}>
          <button
            onClick={(e) => { e.stopPropagation(); onPreview(); }}
            className={styles['hover-preview-btn']}
            title="Preview"
          >
            <Eye className={styles['icon-xs']} />
          </button>
          {eventStatus === 'active' && canManage && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className={styles['hover-delete-btn']}
              title="Delete"
            >
              <Trash2 className={styles['icon-xs']} />
            </button>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className={styles['card-footer']}>
        <p className={styles['card-name']}>{overlay.name}</p>
        <div className={styles['card-footer-row']}>
          <p className={styles['card-type']}>PNG</p>

          {/* Mobile-only persistent action buttons */}
          <div className={styles['mobile-actions']}>
            <button
              onClick={(e) => { e.stopPropagation(); onPreview(); }}
              className={styles['mobile-action-btn']}
              aria-label={`Preview ${overlay.name}`}
            >
              <Eye className={styles['icon-xs']} />
            </button>
            {eventStatus === 'active' && canManage && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className={styles['mobile-delete-btn']}
                aria-label={`Delete ${overlay.name}`}
              >
                <Trash2 className={styles['icon-xs']} />
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ── Upload card ─────────────────────────────────────────────────────────────

function UploadCard({ onClick }: { onClick: () => void }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={{ scale: 0.96 }}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.15 }}
      className={styles['upload-card']}
    >
      <div className={styles['upload-card-thumbnail']}>
        <div className={styles['upload-card-icon-wrap']}>
          <Upload className={styles['icon-md']} />
        </div>
      </div>
      <div className={styles['card-footer']}>
        <p className={styles['card-name']}>Upload Overlay</p>
        <div className={styles['card-footer-row']}>
          <p className={styles['card-type']}>Add PNG</p>
        </div>
      </div>
    </motion.button>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export const OverlayManager: React.FC<OverlayManagerProps> = ({ eventId, eventStatus, canManage = true }) => {
  const [overlays, setOverlays] = useState<Overlay[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [overlayName, setOverlayName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [search, setSearch] = useState('');
  const [previewTarget, setPreviewTarget] = useState<Overlay | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadOverlays(); }, [eventId]);

  // Generate (and clean up) a local preview URL for the selected file
  useEffect(() => {
    if (!selectedFile) {
      setFilePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(selectedFile);
    setFilePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const loadOverlays = async () => {
    try {
      setLoading(true);
      const data = await overlaysApi.list(eventId);
      // Never surface the default Pixel Machha branding overlay — it's applied
      // automatically and must not be listed, counted, or deletable by anyone.
      setOverlays(data.filter((o) => o.storagePath !== DEFAULT_OVERLAY_S3_PATH));
    } catch {
      setError('Failed to load overlays');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile || !overlayName.trim()) return;
    setUploading(true);
    setError('');
    try {
      const overlay = await overlaysApi.upload(eventId, overlayName.trim(), selectedFile);
      setOverlays((prev) => [overlay, ...prev]);
      resetUploadForm();
      track(EVENTS.OVERLAY_UPLOADED, { event_id: eventId, overlay_name: overlayName.trim() });
      toast.success('Overlay uploaded');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      const msg = e.response?.data?.error || 'Failed to upload overlay';
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await overlaysApi.delete(eventId, deleteTarget);
      setOverlays((prev) => prev.filter((o) => o._id !== deleteTarget));
      setDeleteTarget(null);
      track(EVENTS.OVERLAY_DELETED, { event_id: eventId });
      toast.success('Overlay deleted');
    } catch {
      setError('Failed to delete overlay');
      toast.error('Failed to delete overlay');
    } finally {
      setDeleting(false);
    }
  };

  const resetUploadForm = () => {
    setOverlayName('');
    setSelectedFile(null);
    setShowUpload(false);
    setError('');
  };

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file?.type === 'image/png') {
      setSelectedFile(file);
      setOverlayName(file.name.replace(/\.png$/i, ''));
      setShowUpload(true);
    } else {
      toast.error('Only PNG files are supported');
    }
  }, []);

  const filteredOverlays = overlays.filter((o) =>
    o.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      {/* ── Search bar ── */}
      {!loading && overlays.length > 0 && (
        <div className={styles['search-bar']}>
          <div className={styles['search-wrapper']}>
            <Search className={`${styles['search-icon']} ${styles['icon-sm']}`} />
            <input
              type="text"
              placeholder="Search overlays"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={styles['search-input']}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className={styles['search-clear']}
              >
                <X className={styles['icon-sm']} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Content ── */}
      {loading ? (
        <div className={styles['overlay-grid']}>
          {Array.from({ length: 6 }).map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: i * 0.05 }}
            >
              <OverlayCardSkeleton />
            </motion.div>
          ))}
        </div>

      ) : overlays.length === 0 ? (
        /* Empty state */
        <div
          onDragOver={(e) => { e.preventDefault(); if (eventStatus === 'active') setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={eventStatus === 'active' ? handleFileDrop : undefined}
          className={[
            styles['empty-drop-zone'],
            dragOver ? styles['empty-drop-zone-active'] : '',
          ].filter(Boolean).join(' ')}
        >
          <div className={styles['empty-inner']}>
            <div className={styles['empty-illustration']}>
              <MachhaCharacter state="Empty" part="body" size={120} />
            </div>
            <div>
              <p className={styles['empty-title']}>
                No overlays yet
              </p>
              <p className={styles['empty-text']}>
                {eventStatus === 'active'
                  ? 'Drag & drop PNG files here, or tap Upload to add your first overlay.'
                  : 'No overlays were added to this event.'}
              </p>
              {eventStatus === 'active' && canManage && (
                <Button variant="constructive" size="sm" onClick={() => setShowUpload(true)}>
                  <Upload className={styles['icon-sm']} />
                  Upload Overlay
                </Button>
              )}
            </div>
          </div>
        </div>

      ) : filteredOverlays.length === 0 ? (
        <div className={styles['no-results']}>
          <p className={styles['no-results-text']}>
            No overlays match &ldquo;{search}&rdquo;
          </p>
          <button
            onClick={() => setSearch('')}
            className={styles['clear-search-btn']}
          >
            Clear search
          </button>
        </div>

      ) : (
        <div className={styles['overlay-grid']}>
          {eventStatus === 'active' && canManage && !search && (
            <UploadCard onClick={() => setShowUpload(true)} />
          )}
          <AnimatePresence>
            {filteredOverlays.map((overlay) => (
              <OverlayCard
                key={overlay._id}
                overlay={overlay}
                eventStatus={eventStatus}
                canManage={canManage}
                onPreview={() => setPreviewTarget(overlay)}
                onDelete={() => setDeleteTarget(overlay._id)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* ── Upload Modal ── */}
      <Modal isOpen={showUpload} onClose={resetUploadForm} title="Upload Overlay">
        <form onSubmit={handleUpload} className={styles['upload-form']}>
          <Input
            label="Overlay Name"
            placeholder="e.g. Event Logo, Watermark"
            value={overlayName}
            onChange={(e) => setOverlayName(e.target.value)}
            required
            autoFocus
          />
          <div className={styles['upload-form-file-section']}>
            <label className={styles['file-label']}>PNG File</label>
            <div
              className={[
                styles['drop-area'],
                selectedFile ? styles['drop-area-selected'] : '',
              ].filter(Boolean).join(' ')}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files[0];
                if (file?.type === 'image/png') setSelectedFile(file);
                else toast.error('Only PNG files are supported');
              }}
            >
              {selectedFile ? (
                <div className={styles['drop-selected-inner']}>
                  <div
                    className={`${styles['drop-preview']} ${styles['checkerboard-bg']}`}
                    style={{ '--checkerboard': CHECKERBOARD } as React.CSSProperties}
                  >
                    {filePreviewUrl && (
                      <img src={filePreviewUrl} alt={selectedFile.name} className={styles['drop-preview-img']} />
                    )}
                  </div>
                  <div className={styles['drop-selected-meta']}>
                    <svg className={styles['drop-check-icon']} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className={styles['drop-selected-name']}>{selectedFile.name}</span>
                  </div>
                  <p className={styles['drop-replace-hint']}>Click to choose a different file</p>
                </div>
              ) : (
                <div className={styles['drop-placeholder']}>
                  <Upload className={styles['drop-upload-icon']} />
                  <p>Drag & drop or <span className={styles['drop-browse-link']}>click to browse</span></p>
                  <p className={styles['drop-hint']}>PNG files only</p>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png"
              className={styles['hidden-input']}
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            />
          </div>
          {error && <p className={styles['upload-error']}>{error}</p>}
          <div className={styles['upload-actions']}>
            <Button variant="ghost" type="button" onClick={resetUploadForm}>Cancel</Button>
            <Button type="submit" loading={uploading} disabled={!selectedFile || !overlayName.trim()}>Upload</Button>
          </div>
        </form>
      </Modal>

      {/* ── Preview Modal ── */}
      <Modal isOpen={!!previewTarget} onClose={() => setPreviewTarget(null)} title={previewTarget?.name ?? 'Preview'}>
        <div className={styles['preview-body']}>
          <div
            className={`${styles['preview-container']} ${styles['checkerboard-bg']}`}
            style={{ '--checkerboard': CHECKERBOARD } as React.CSSProperties}
          >
            {previewTarget && (
              <img
                src={overlaysApi.fileUrl(previewTarget._id)}
                alt={previewTarget.name}
                className={styles['preview-img']}
              />
            )}
          </div>
        </div>
      </Modal>

      {/* ── Delete Confirmation Modal ── */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Overlay">
        <div className={styles['delete-body']}>
          <p className={styles['delete-text']}>
            Are you sure you want to delete this overlay? This action cannot be undone.
          </p>
          <div className={styles['delete-actions']}>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="danger" loading={deleting} onClick={handleDelete}>Delete</Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
